import { ComplianceCalendarEventStatus } from 'mapistry-shared';

type SortableCalendarEvent =
  | { isLoadingIndicator: true }
  | {
      isLoadingIndicator?: false;
      status: ComplianceCalendarEventStatus;
      title: string;
    };

const sortAlphabetically = (
  eventA: SortableCalendarEvent,
  eventB: SortableCalendarEvent,
) => {
  if (eventA.isLoadingIndicator || eventB.isLoadingIndicator) return 0;
  return eventA.title.localeCompare(eventB.title);
};

const getStatusSortOrder = (status: ComplianceCalendarEventStatus): number => {
  switch (status) {
    case ComplianceCalendarEventStatus.OVERDUE_BUT_STARTED:
      return 0;
    case ComplianceCalendarEventStatus.OVERDUE_NOT_STARTED:
      return 1;
    case ComplianceCalendarEventStatus.INCOMPLETE_BUT_STARTED:
      return 2;
    case ComplianceCalendarEventStatus.INCOMPLETE_NOT_STARTED:
      return 3;
    case ComplianceCalendarEventStatus.FUTURE_INACTIVE:
      return 4;
    case ComplianceCalendarEventStatus.EXCEPTION:
      return 5;
    case ComplianceCalendarEventStatus.NOT_NEEDED:
      return 6;
    case ComplianceCalendarEventStatus.COMPLETE:
      return 7;
    default: {
      const exhaustiveCheck: never = status;
      throw new Error(`Unhandled calendar event status: ${exhaustiveCheck}`);
    }
  }
};

export const sortByStatusThenAlphabetically = (
  eventA: SortableCalendarEvent,
  eventB: SortableCalendarEvent,
) => {
  if (eventA.isLoadingIndicator || eventB.isLoadingIndicator) return 0;

  const [aStatusSortOrder, bStatusSortOrder] = [
    getStatusSortOrder(eventA.status),
    getStatusSortOrder(eventB.status),
  ];

  if (aStatusSortOrder === bStatusSortOrder) {
    return sortAlphabetically(eventA, eventB);
  }
  if (aStatusSortOrder < bStatusSortOrder) {
    return -1;
  }
  return 1;
};

export const sortEventsWithinDay = (eventA: unknown, eventB: unknown) =>
  sortByStatusThenAlphabetically(
    // FullCalendar seems to not expose the internal types I'd need to type this right
    eventA as SortableCalendarEvent,
    eventB as SortableCalendarEvent,
  );
